import { createEffect, For, onCleanup, reactive } from 'cdui-js';
import { UploadLic } from '../infomations/UploadLic';
import { Address, buildAddressSavePayload } from '../infomations/Address';
import { JobStatus } from '../infomations/JobStatus';
import { FinancialStatus, buildFinancePayload } from '../infomations/FinancialStatus';
import { InvestmentExp, buildInvestmentExpPayload } from '../infomations/InvestmentExp';
import { TaxInfo } from '../infomations/TaxInfo';
import { Others, buildOthersPayload } from '../infomations/Others';
import { ActivateEleServices } from '../infomations/ActivateEleServices';
import {
  resolveNeedDerivativeTradePermission,
  SelectAccount,
} from '../infomations/SelectAccount';
import { AddBankAccount, buildOpenAccountBankBindPayload } from './AddBankAccount';
import { BankInfoConfirm } from './BankInfoConfirm';
import { syncTaxFieldsFromLic } from '../infomations/taxSync';

import {
  bingBankAcc,
  createW8ben,
  saveAccount,
  saveApply,
  saveCareer,
  saveUserInfo,
  submitApply,
} from '../../../../../../api/open-account';
import {
  getApplySaveExtra,
  clearReEditFormBindings,
  getFormApi,
  getFormValidateFunc,
  pState,
  refreshApplyIfIdentityChanged,
  restoreReEditFormBindings,
  setFormApi,
  setFormValidateFunc,
} from '../../store';
import { aesEncryptHexByPass } from '../../../../../../lib/crypto';
import { commonState, getEffectiveSafeBottom } from '../../../../../../stores/common';
import { normalizeOpinionItems } from '../../errorKeyNormalize';
import i18n from '../../../../../../i18n';
import { replaceTemplate } from '@china-difi/lib/template';
import dayts from '@china-difi/lib/date';
import { getBarBottomOffset, keyboardAvoidState, setKeyboardAvoidScrollRoot } from '../../keyboardAvoid';
import { setBarPortalSuppressed, setBarTransitioning, waitForOpenProgressPaint } from '../../components/barSlot';
import { OpenProgressBarSpinner } from '../../components/LastNextBtns';
import { showToast } from '../../../../components/Toast';

/** 与开户 LastNextBtns / OnlineProgress 底栏预留一致 */
const LAST_NEXT_BAR_BODY = 80;
const lastNextBarReservePx = () => LAST_NEXT_BAR_BODY + getEffectiveSafeBottom();

export const saveFuncMap = {
  2: async () => {
    const identityType = pState.uploadLicForm.idCardType;
    pState.uploadLicForm.identityType = identityType;
    const data = await saveUserInfo({ identityType, idCardType: identityType, progress: 2 });
    if (!pState.id) {
      pState.id = data.id;
    }
    await refreshApplyIfIdentityChanged(identityType);
  },
  3: async () => {
    const data = JSON.parse(JSON.stringify(pState.uploadLicForm));
    data.identityType = data.identityType || data.idCardType;
    console.log('[encryptKey][beforeEncrypt][EditErrorInfo][idCardNo]', {
      encryptKey: commonState.encryptKey || '(empty)',
      plainLen: String(data.idCardNo || '').length,
    });
    data.idCardNo = aesEncryptHexByPass(data.idCardNo, commonState.encryptKey);
    await saveUserInfo({ ...data, progress: 3 });
    // 退回修改证件后同步税务国家/编号，与证件保持一致
    syncTaxFieldsFromLic();
    await saveCareer({ ...pState.taxInfoForm, progress: 8 });
  },
  4: async () => {
    // 未勾选「住址证明是中文」时 addressCn 传空（与 Address 页下一步一致）
    const data = buildAddressSavePayload();
    await saveUserInfo({ ...data, progress: 4 });
  },
  5: async () => {
    const data = { ...pState.jobStatusForm };
    if (data.industry !== 'OTHER') data.industryOther = '';
    if (data.position !== 'OTHER') data.positionOther = '';
    await saveCareer({ ...data, progress: 5 });
  },
  6: async () => {
    await saveCareer(buildFinancePayload());
  },
  7: async () => {
    await saveCareer(buildInvestmentExpPayload());
  },
  8: async () => {
    await createW8ben({
      id: pState.id,
      signature: pState.uploadLicForm.fullName,
      signatureDate: dayts.format(Date.now()),
    });
    await saveCareer({ ...pState.taxInfoForm, progress: 8 });
  },
  9: async () => {
    await saveCareer(buildOthersPayload());
  },
  10: async () => {
    let list = [];
    Object.keys(pState.activateEleServicesForm.list).forEach((key) => {
      list.push(...(pState.activateEleServicesForm.list[key]?.list || []));
    });
    const selectedList = list.filter((i) => i.check);
    const electronicServices = selectedList.map((i) => i.value);
    await saveApply({
      electronicServices: electronicServices.toString(),
      progress: 10,
      applyType: 'NEW_APPLY',
      ...getApplySaveExtra(),
    });
    pState.activateEleServicesForm.selected = electronicServices.toString();
  },
  11: async () => {
    const securSelect = pState.selectAccountForm.securitiesList.filter((i) => i.check);
    const fundSelect = pState.selectAccountForm.fundList.filter((i) => i.check);
    const tradeSelect = (pState.selectAccountForm.tradePermissionList || []).filter((i) => i.check);
    const needDerivativeTradePermission = resolveNeedDerivativeTradePermission(tradeSelect.length);
    await saveAccount({
      securitiesAccountType: securSelect.map((i) => i.value).toString(),
      fundAccountType: fundSelect.map((i) => i.value).toString(),
      needDerivativeTradePermission,
      progress: 11,
    });
    await saveApply({
      needDerivativeTradePermission,
      applyType: 'NEW_APPLY',
    });
    pState.selectAccountForm.needDerivativeTradePermission = needDerivativeTradePermission;
  },
  18: async () => {
    await saveAccount({
      accountType: pState.currency.accountType,
      progress: 18,
    });
  },
  19: async () => {
    const data = await buildOpenAccountBankBindPayload();
    if (!data) throw new Error('OPEN_ACCOUNT_BANK_NAME_REQUIRED');
    console.log('[encryptKey][beforeEncrypt][EditErrorInfo][bankAccountNo]', {
      encryptKey: commonState.encryptKey || '(empty)',
      plainLen: String(data.bankAccountNo || '').length,
    });
    data.bankAccountNo = aesEncryptHexByPass(data.bankAccountNo, commonState.encryptKey);
    await bingBankAcc({ ...data, progress: 19 });
  },
  20: async () => {
    await submitApply({ id: pState.id, progress: 20 });
    if (pState.auditInfo) {
      pState.auditInfo.status = 'SUBMITTED';
    }
  },
};

// IDENTITY_FRONT("identity_front", "身份证正面"),
// IDENTITY_BACK("identity_back", "身份证反面"),
// HONGKONG_IDENTITY("hongkong_identity", "香港身份证"),
// MACAO_IDENTITY("macao_identity", "澳门身份证"),
// PASSPORT("passport", "护照"),
// ADDRESS_PROVE("address_prove", "地址证明"),
// BANK_PROVE("bank_prove", "银行证明"),
// TRANSFER_PROVE("transfer_prove", "转账证明"),
// W8_BEN("w8_ben", "W8-BEN税务表格pdf文件"),
// AML("aml", "AML文件"),

export function findPageNamesByErrors(components, errors) {
  let list = errors;
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list) || list.length === 0) return [];
  const normalized = normalizeOpinionItems(list);
  const errorKeySet = new Set(normalized.map((e) => e.key));
  const result = [];
  const seen = new Set();
  for (const c of components) {
    const hit = c.keys.some((k) => errorKeySet.has(k));
    if (hit && !seen.has(c.name)) {
      seen.add(c.name);
      result.push(c);
    }
  }
  return result;
}

const components = [
  {
    name: 'UploadLic',
    component: UploadLic,
    keys: [
      'firstNameEn',
      'lastNameEn',
      'fullName',
      'pinyinName',
      'gender',
      'idCardType',
      'idCardNo',
      'idCardStartDate',
      'idCardEndDate',
      'idCardLongTerm',
      'idCardIssueAuthority',
      'birthDate',
      'identity',
      'identity_front',
      'identity_back',
      'hongkong_identity',
      'macao_identity',
      'passport',
    ],
    func: () => saveFuncMap[3]?.(),
  },
  {
    name: 'Address',
    component: Address,
    keys: ['country', 'address', 'addressCn', 'correspondenceAddress', 'addressType', 'addressTypeOther', 'phone', 'email', 'address_prove'],
    func: () => saveFuncMap[4]?.(),
  },
  {
    name: 'JobStatus',
    component: JobStatus,
    keys: [
      'education',
      'employmentStatus',
      'companyName',
      'industry',
      'industryOther',
      'position',
      'positionOther',
      'companyAddress',
      'employmentYears',
      'companyPhone',
    ],
    func: () => saveFuncMap[5]?.(),
  },
  {
    name: 'FinancialStatus',
    component: FinancialStatus,
    keys: [
      'housingProperty',
      'annualIncome',
      'totalAssetsNetHkd',
      'wealthSource',
      'wealthSourceOther',
      'sourceOfFunds',
      'sourceOfFundsOther',
      'fundTransferMethod',
      'sourceOfFundsLocation',
      'sourceOfFundsLocationOther',
    ],
    func: () => saveFuncMap[6]?.(),
  },
  {
    name: 'InvestmentExp',
    component: InvestmentExp,
    keys: [
      'investmentKnowledge',
      'investmentExperienceTypes',
      'investmentTarget',
      'investmentPurposeOther',
      'stockExperience',
      'futuresExperience',
      'optionsExperience',
      'warrantBullBearExperience',
      'fxExperience',
      'bullionExperience',
      'fundStructuredProductExperience',
      'virtualAssetExperience',
      'hasDerivativeKnowledge',
      'hasDerivativeTraining',
      'hasDerivativeWorkExperience',
      'hasDerivativeTrade5times',
      'intendTradeDerivative',
      // 'riskLevel',
    ],
    func: () => saveFuncMap[7]?.(),
  },
  {
    name: 'TaxInfo',
    component: TaxInfo,
    keys: ['isUsPerson', 'bankAccount', 'bankBranchName', 'country', 'fpsIdentificationCode', 'taxNumber', 'w8_ben'],
    func: () => saveFuncMap[8]?.(),
  },
  {
    name: 'Others',
    component: Others,
    keys: [
      'isSfcLicensed',
      'sfcLicenseDesc',
      'isRelativeToStaff',
      'staffRelativeDesc',
      'isSpouseHoldCompanyAccount',
      'spouseHoldCompanyAccountDesc',
      'spouseHoldName',
      'spouseHoldAccountNo',
      'isControlOtherClientVoting35',
      'controlOtherClientVoting35Desc',
      'controlVotingAccountName',
      'controlVotingAccountNo',
    ],
    func: () => saveFuncMap[9]?.(),
  },
  {
    name: 'ActivateEleServices',
    component: ActivateEleServices,
    keys: ['electronicServices'],
    func: () => saveFuncMap[10]?.(),
  },
  {
    name: 'SelectAccount',
    component: SelectAccount,
    keys: ['securitiesAccountType', 'fundAccountType', 'needDerivativeTradePermission'],
    func: () => saveFuncMap[11]?.(),
  },
  // {
  //   name: 'SelectCurrency',
  //   component: SelectCurrency,
  //   keys: [],
  //   func: () => saveFuncMap[18]?.(),
  // },
  {
    name: 'AddBankAccount',
    component: AddBankAccount,
    keys: ['bankCode', 'bankName', 'bankAccountNo', 'bankAccountName', 'bankAccountType', 'bank_prove', 'accountNo', 'bank'],
    func: () => saveFuncMap[19]?.(),
  },
  {
    name: 'BankInfoConfirm',
    component: BankInfoConfirm,
    keys: ['transfer_prove', 'transfer'],
  },
];

/**
 * EditErrorInfo
 *
 * 两种使用场景：
 *   1. 模态弹层（Reviewing.tsx）：props.close() 关闭遮罩
 *   2. 全屏独立页（editRefused 模式）：props.onComplete() 通知父层进入下一阶段（大陆 risk / 非大陆 sign）
 *
 * 注意：App 的 APP_TO_WEB_EditOpinion / getByUserId 可能晚于本组件挂载才写入
 * pState.errorValues。list 必须每次从响应式源重算，禁止挂载时一次性缓存。
 */
export const EditErrorInfo = (props) => {
  const isStandalone = !!props?.onComplete;

  // 自管底栏期间隐藏全局 Portal，避免与纠错「下一步」抢点击
  setBarPortalSuppressed(true);
  onCleanup(() => {
    setBarPortalSuppressed(false);
    setKeyboardAvoidScrollRoot(null);
    clearReEditFormBindings();
  });

  /** 模态用 props.errors；standalone 读 pState（App / 接口后到也能追踪） */
  const getErrors = () => {
    if (props?.errors != null) return props.errors;
    return pState.errorValues;
  };

  const getList = () => findPageNamesByErrors(components, getErrors());

  const state = reactive({
    step: 0,
    saving: false,
    /** next：右侧转圈；prev：两侧只禁用，上一步不转圈 */
    barSide: '' as '' | 'next' | 'prev',
  });
  /** 非响应式：避免 createEffect 读写同一 signal 造成重入 */
  let didInitStep = false;

  const currentName = () => getList()[state.step]?.name ?? '';

  const bindStep = (step: number) => {
    restoreReEditFormBindings(getList()[step]?.name ?? '');
    state.step = step;
  };

  // 首屏在 Switch 创建子页之前记下页名，表单 ref 才能按页写入缓存
  restoreReEditFormBindings(currentName());

  // opinion 到位或 list 变长后：校正 step（含 editRefused 返回 forms 恢复下标）
  createEffect(() => {
    const list = getList();
    if (list.length === 0) return;
    if (!didInitStep) {
      didInitStep = true;
      if (isStandalone) {
        bindStep(Math.min(Math.max(pState.editRefusedFormStep, 0), list.length - 1));
      }
      return;
    }
    if (state.step >= list.length) {
      bindStep(list.length - 1);
    }
  });

  /**
   * 按页名挂载。组件函数体只跑一次，不能用一次性的 Comp 变量。
   * 不能放进 `<If>` 的 memo：子页 onMount 改列表会同步重入该 memo，炸栈。
   * 用 `<For each={[pageName]}>`：只订阅页名；列表写入只更新页内 For，不拆外层。
   * 切页时 mapArray 按字符串 key 换页并重挂载，formApi 会重新注册。
   */
  const stepPages = () => {
    const name = currentName();
    return name ? [name] : [];
  };

  const StepView = () => (
    <For each={stepPages()}>
      {(name) => {
        if (name === 'UploadLic') return <UploadLic reEdit />;
        if (name === 'Address') return <Address reEdit />;
        if (name === 'JobStatus') return <JobStatus reEdit />;
        if (name === 'FinancialStatus') return <FinancialStatus reEdit />;
        if (name === 'InvestmentExp') return <InvestmentExp reEdit />;
        if (name === 'TaxInfo') return <TaxInfo reEdit />;
        if (name === 'Others') return <Others reEdit />;
        if (name === 'ActivateEleServices') return <ActivateEleServices reEdit />;
        if (name === 'SelectAccount') return <SelectAccount reEdit />;
        if (name === 'AddBankAccount') return <AddBankAccount reEdit />;
        if (name === 'BankInfoConfirm') return <BankInfoConfirm reEdit />;
        return null;
      }}
    </For>
  );

  let scrollRoot: HTMLDivElement | null = null;

  const setStep = (step: number) => {
    bindStep(step);
    if (isStandalone) pState.editRefusedFormStep = step;
    if (scrollRoot) scrollRoot.scrollTop = 0;
  };

  const isLastFormStep = () => state.step === getList().length - 1;

  const handlePrev = () => {
    if (state.saving || state.step <= 0) return;
    state.saving = true;
    state.barSide = 'prev';
    setBarTransitioning(true);
    const fromStep = state.step;
    void (async () => {
      try {
        setFormApi(null);
        setFormValidateFunc(null);
        setStep(state.step - 1);
        if (state.step !== fromStep) await waitForOpenProgressPaint();
      } finally {
        state.saving = false;
        state.barSide = '';
        setBarTransitioning(false);
      }
    })();
  };

  /**
   * 先 await Form 校验；通过后 await 保存成功，再推进下一步 / 完成。
   * 保存失败或校验失败则停留当前页并立刻解锁。
   * 已经切页（含末步进入风险披露）则锁到目标页画出。
   */
  const handleNext = async () => {
    if (state.saving) return;

    state.saving = true;
    state.barSide = 'next';
    setBarTransitioning(true);
    const fromStep = state.step;
    const fromPhase = pState.editRefusedPhase;
    try {
      const list = getList();
      const current = list[state.step];
      const currentFunc = current?.func;
      const lastStep = isLastFormStep();

      const formApi = getFormApi();
      const formValidateFunc = getFormValidateFunc();
      if (formApi) {
        const valid = await formApi.validate();
        if (!valid) {
          showToast(i18n.OpenProgress?.Common?.ToastIncomplete || '请完善必填信息');
          formApi.scrollToError?.();
          return;
        }
      }

      if (formValidateFunc) {
        const valid = await formValidateFunc.callback();
        if (!valid) return;
      }

      if (currentFunc) {
        await currentFunc();
      }

      // 切换步骤前清空，避免无 Form 的页沿用上一页的 api
      setFormApi(null);
      setFormValidateFunc(null);

      if (lastStep) {
        if (isStandalone) {
          // 保留最后一页下标，video → forms 回退时落在「上一页」而非第 1 页
          pState.editRefusedFormStep = state.step;
          props.onComplete?.();
        } else {
          props?.close?.();
        }
      } else {
        setStep(state.step + 1);
      }

      const moved = state.step !== fromStep || pState.editRefusedPhase !== fromPhase;
      if (moved) await waitForOpenProgressPaint();
    } catch {
      // 保存失败：停留当前页；错误提示由 http 层处理
    } finally {
      state.saving = false;
      state.barSide = '';
      setBarTransitioning(false);
    }
  };

  return (
    <div
      class="edit-error-info fixed position-t position-b position-l position-r"
      style={{ background: 'var(--bg-page)', 'z-index': '160' }}
    >
      <div style={{ height: '100%', width: '100%', 'min-height': '0' }} class="flex flex-column">
        {/* NavBar */}
        <div
          class="padding-L flex align-center justify-space-between"
          style={{ 'z-index': '161', position: 'relative' }}
        >
          {/* <Icon
            name="common-back-arrow"
            class="icon-c-D icon-s-XL"
            onclick={() => {
              if (state.step > 0) {
                state.step = state.step - 1;
              } else {
                props?.close?.();
              }
            }}
          /> */}
          <div class="font-s-L color-D" style={{ 'text-align': 'center' }}>
            {replaceTemplate(i18n.OpenProgress.EditError.NavTitle, state.step + 1, getList().length)}
          </div>
          <div></div>
        </div>

        {/*
          底栏 position:fixed，不占文档流。这里用等高占位把滚动区停在按钮上方。
          不要写 padding-bottom：setKeyboardAvoidScrollRoot 会写/清这条内联样式。
          键盘打开时收起占位（翻转一次），滚动区吃满剩余高度，底部 input 才滚得上去。
        */}
        <div
          class="open-progress-scroll-root"
          ref={(el: HTMLDivElement | null) => {
            scrollRoot = el;
            setKeyboardAvoidScrollRoot(el);
          }}
          style={{
            flex: '1',
            'min-height': '0',
            'overflow-y': 'auto',
            '-webkit-overflow-scrolling': 'touch',
          }}
        >
          <StepView />
        </div>
        <div
          style={{
            flex: 'none',
            height: keyboardAvoidState.keyboardOpen ? '0px' : `${lastNextBarReservePx()}px`,
          }}
        />

        {state.saving && (
          <div
            class="open-progress-submit-mask fixed position-t position-b position-l position-r"
            style={{ 'z-index': 160 }}
            onclick={(e) => e.stopPropagation()}
          />
        )}

        {/*
          自管底栏：barSlot.suppressed 已关闭 Portal；z-index 高于 Portal(150)。
          键盘打开时用 visibility 藏起（不卸载、不改占位高度），避免布局跳变抖屏。
        */}
        <div
          class="flex align-center bg-c page-foot"
          style={{
            position: 'fixed',
            bottom: `${getBarBottomOffset()}px`,
            left: '0',
            width: '100%',
            height: `${lastNextBarReservePx()}px`,
            /* 覆盖 .page-foot 的 padding-top:16，与开户底栏同为 0 + 左右 16 + 底安全区 */
            padding: `0 16px ${getEffectiveSafeBottom()}px`,
            gap: '10px',
            'z-index': '161',
            'border-top': '1px solid var(--border-c)',
            'box-sizing': 'border-box',
            visibility: keyboardAvoidState.keyboardOpen ? 'hidden' : 'visible',
            'pointer-events': keyboardAvoidState.keyboardOpen ? 'none' : 'auto',
          }}
        >
          {state.step > 0 && (
            <button
              type="button"
              class="default-btn btn-rounded"
              style={{ height: '50px', flex: 1 }}
              disabled={state.saving}
              onclick={handlePrev}
            >
              {i18n.OpenProgress.Common.Prev}
            </button>
          )}

          <button
            type="button"
            class="primary-btn btn-rounded"
            style={{ height: '50px', flex: 2 }}
            disabled={state.saving}
            onclick={() => void handleNext()}
          >
            {state.saving && state.barSide !== 'prev' ? (
              <OpenProgressBarSpinner />
            ) : (
              i18n.OpenProgress.Common.Next
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
